import React, { useCallback, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'localization';
import { ADMIN_NAV_SECTIONS, AdminSection } from './sections';
import './MetasphereAdminShell.scss';

const SIDEBAR_NAV_CLASS = 'metasphere-admin-shell__nav-item';
const SIDEBAR_NAV_ACTIVE_CLASS = `${SIDEBAR_NAV_CLASS} ${SIDEBAR_NAV_CLASS}--active`;

const sectionToClassName = ({ isActive }: { isActive: boolean }): string =>
  isActive ? SIDEBAR_NAV_ACTIVE_CLASS : SIDEBAR_NAV_CLASS;

interface MetasphereAdminShellProps {
  sections?: AdminSection[];
}

const MetasphereAdminShell: React.FC<MetasphereAdminShellProps> = ({
  sections = ADMIN_NAV_SECTIONS,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((open) => !open);
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  return (
    <div
      className={`metasphere-admin-shell${
        sidebarOpen ? ' metasphere-admin-shell--sidebar-open' : ''
      }`}
    >
      <button
        type="button"
        className="metasphere-admin-shell__sidebar-toggle"
        aria-label={t('admin.shell.toggleSidebar')}
        aria-expanded={sidebarOpen}
        onClick={toggleSidebar}
      >
        <i className="pi pi-bars" aria-hidden="true" />
      </button>

      <aside
        className="metasphere-admin-shell__sidebar"
        aria-label={t('admin.shell.sidebarLabel')}
      >
        <div className="metasphere-admin-shell__sidebar-header">
          <button
            type="button"
            className="metasphere-admin-shell__home-btn"
            onClick={() => navigate('/')}
            aria-label={t('admin.shell.goHome', 'Go to home')}
          >
            <i className="pi pi-arrow-left" aria-hidden="true" />
          </button>
          <h2 className="metasphere-admin-shell__title">
            {t('admin.shell.title')}
          </h2>
        </div>
        <nav
          className="metasphere-admin-shell__nav"
          aria-label={t('admin.shell.navLabel')}
        >
          {sections.map((section) => (
            <NavLink
              key={section.id}
              to={section.id}
              className={sectionToClassName}
              onClick={closeSidebar}
              data-testid={`admin-nav-${section.id}`}
            >
              <i
                className={`${section.icon} metasphere-admin-shell__nav-icon`}
                aria-hidden="true"
              />
              <span className="metasphere-admin-shell__nav-label">
                {t(section.labelKey)}
              </span>
            </NavLink>
          ))}
        </nav>
      </aside>

      <main className="metasphere-admin-shell__main" role="main">
        <Outlet />
      </main>
    </div>
  );
};

export default MetasphereAdminShell;
